import React, { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import { CCard, CCardBody, CButton, CFormLabel } from "@coreui/react";
import { FaPlay, FaStop, FaTrash } from "react-icons/fa";
import AppMessage from "./AppMessage";

const VIDEO_W = 400;
const VIDEO_H = 225;

const LiveVideoCapture = ({
  onVideoCaptured,
  productName,
  readonly = false,
  defaultVideoUrl,
}) => {
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(defaultVideoUrl || null);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);
  const [prompt, setPrompt] = useState("");

  // Generate prompt
  useEffect(() => {
    const today = new Date().toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    setPrompt(
      productName
        ? `Say: "Today is ${today}, and I am uploading ${productName}."`
        : `Say: "Today is ${today}, and I am uploading a new product."`
    );
  }, [productName]);

  // Update preview when parent sends new defaultVideoUrl
  useEffect(() => {
    if (defaultVideoUrl) setPreviewUrl(defaultVideoUrl);
  }, [defaultVideoUrl]);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const startTimer = () =>
    (timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000));
  const stopTimer = () => {
    clearInterval(timerRef.current);
    setSeconds(0);
  };
  const mmss = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(
      2,
      "0"
    )}`;

  const handleStart = () => {
    if (!webcamRef.current?.stream) {
      alert("Webcam not ready. Allow access and refresh.");
      return;
    }
    setVideoBlob(null);
    setPreviewUrl(null);

    const chunks = [];
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: "video/webm",
    });
    mediaRecorderRef.current.ondataavailable = (e) =>
      e.data.size && chunks.push(e.data);
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setVideoBlob(blob);
      setPreviewUrl(url);

      // Geolocation
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          onVideoCaptured({
            blob,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            captured_at: new Date().toISOString().slice(0, 19),
          }),
        () =>
          onVideoCaptured({
            blob,
            latitude: null,
            longitude: null,
            captured_at: new Date().toISOString().slice(0, 19),
          })
      );
    };

    mediaRecorderRef.current.start();
    setRecording(true);
    startTimer();
  };

  const handleStop = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    stopTimer();
  };

  const handleDelete = () => {
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setVideoBlob(null);
    setPreviewUrl(null);
    onVideoCaptured({ deleted: true });
  };

  // if(readonly){
  // return (
  //   <CCard className="mb-4">
  //     <CCardBody>
  //       <AppMessage message={prompt} />
  //       <div className="d-flex flex-column align-items-center my-3">
  //         {!previewUrl ? (
  //           <>
  //             <Webcam
  //               key={previewUrl ? "preview" : "live"}
  //               ref={webcamRef}
  //               mirrored
  //               audio
  //               videoConstraints={{ width: VIDEO_W, height: VIDEO_H }}
  //               style={{
  //                 width: VIDEO_W,
  //                 height: VIDEO_H,
  //                 borderRadius: 8,
  //                 border: "1px solid #ccc",
  //               }}
  //             />
  //             {recording && (
  //               <div className="mt-2 text-danger fw-semibold">
  //                 ⏺ Recording • {mmss(seconds)}
  //               </div>
  //             )}
  //           </>
  //         ) : (
  //           <>
  //             <CFormLabel className="d-block mb-2 fw-semibold">
  //               Preview
  //             </CFormLabel>
  //             <video
  //               src={previewUrl}
  //               controls
  //               width={VIDEO_W}
  //               height={VIDEO_H}
  //               style={{ borderRadius: 8, border: "1px solid #ccc" }}
  //             />
  //           </>
  //         )}
  //       </div>

  //       <div className="d-flex justify-content-center gap-3 mt-2">
  //         {!previewUrl && (
  //           <>
  //             <CButton
  //               color="primary"
  //               variant="outline"
  //               onClick={handleStart}
  //               disabled={recording}
  //             >
  //               <FaPlay className="me-2" /> Start
  //             </CButton>
  //             <CButton
  //               color="danger"
  //               variant="outline"
  //               onClick={handleStop}
  //               disabled={!recording}
  //             >
  //               <FaStop className="me-2" /> Stop
  //             </CButton>
  //           </>
  //         )}
  //         {previewUrl && (
  //           <CButton color="dark" variant="outline" onClick={handleDelete}>
  //             <FaTrash className="me-2" /> Delete
  //           </CButton>
  //         )}
  //       </div>
  //     </CCardBody>
  //   </CCard>
  // );
  // }

  if (readonly) {
    return (
      <CCard className="mb-4">
        <CCardBody className="text-center">
          {previewUrl ? (
            <>
              <CFormLabel className="d-block mb-2 fw-semibold">
                Verification Video
              </CFormLabel>
              <video
                src={previewUrl}
                controls
                width={VIDEO_W}
                height={VIDEO_H}
                style={{ borderRadius: 8, border: "1px solid #ccc" }}
              />
            </>
          ) : (
            <div
              style={{
                padding: "1rem",
                color: "#777",
                fontStyle: "italic",
              }}
            >
              No video available.
            </div>
          )}
        </CCardBody>
      </CCard>
    );
  }

  return null;
};

export default LiveVideoCapture;
